/*
 * Outside service keys. The API never answers with a secret, only that one is saved and its first 4 characters.
 */
import { z } from "zod";
import type { CompanyLevel } from "./levels.js";

export const KEY_PROVIDERS = [
  "stripe",
  "stripe_webhook",
  "stripe_publishable",
  "quo",
  "anthropic",
  "google",
  "google_maps",
  "meta",
  "email",
  "google_service",
  "google_ads_token",
  "plaid_client_id",
  "plaid_secret",
  "fee_stripe",
  "fee_stripe_pk",
  "google_client_id",
  "google_client_secret",
  "meta_app_id",
  "meta_app_secret",
] as const;
export type KeyProvider = (typeof KEY_PROVIDERS)[number];
export const keyProvider = z.enum(KEY_PROVIDERS);

/** Each brand and location's own keys: money goes to that business, so each keeps its own Stripe. */
export const COMPANY_KEY_PROVIDERS = ["stripe", "stripe_webhook", "stripe_publishable"] as const satisfies readonly KeyProvider[];
/** Services The Trades runs for every company. Saved at HQ only; every brand and location uses HQ's key. */
/** Plaid is one app for every company (FEATURE-MAP 10): its two keys are HQ's, typed in by the owner only. */
/** fee_stripe: The Trades' own Stripe, where franchises pay the software fee and add-ons (draft 11). */
/** google_service: a Google service account JSON key for Search Console, Google Ads and the Business Profile.
 *  google_ads_token: the Google Ads developer token from the manager account's API Center (2026-10-30). */
/** google_client_id and google_client_secret (a Google OAuth web client), meta_app_id and meta_app_secret (a Meta app):
 *  what each company signs in with to link its own ad accounts, Google profile and Facebook Page (2026-11-01). */
/** google_maps: the browser key the maps load with. Like a Stripe publishable key it is sent to signed-in pages, so in
 *  Google it must be locked to our web addresses and to the Maps JavaScript and Geocoding APIs (2026-10-29). */
export const HQ_KEY_PROVIDERS = [
  "quo",
  "anthropic",
  "google",
  "google_maps",
  "meta",
  "email",
  "google_service",
  "google_ads_token",
  "plaid_client_id",
  "plaid_secret",
  "fee_stripe",
  "fee_stripe_pk",
  "google_client_id",
  "google_client_secret",
  "meta_app_id",
  "meta_app_secret",
] as const satisfies readonly KeyProvider[];

/** Whether a provider's key is run by HQ for everyone (not saved by a brand or location). */
export const isHqKey = (p: KeyProvider): boolean => (HQ_KEY_PROVIDERS as readonly KeyProvider[]).includes(p);

/** The keys a company at this level saves and sees: HQ the services it runs (it has no clients to charge, so no Stripe of
 *  its own; franchise fees use fee_stripe, owner 2026-11-02), the rest only their Stripe. */
export function keyProvidersAt(level: CompanyLevel): readonly KeyProvider[] {
  return level === "hq" ? HQ_KEY_PROVIDERS : COMPANY_KEY_PROVIDERS;
}

/** Keys that show what they are by how they start, so one pasted in the wrong row is caught (owner put the Google sign-in
 *  secret in the Google server key row, 2026-11-02). Only clear marks; a key with none passes. */
const KEY_MARKS: [RegExp, readonly KeyProvider[], string][] = [
  [/^GOCSPX-/, ["google_client_secret"], "Google sign-in client secret"],
  [/\.apps\.googleusercontent\.com$/, ["google_client_id"], "Google sign-in client ID"],
  [/^AIza/, ["google", "google_maps"], "Google API key"],
  [/^\{/, ["google_service"], "Google service account file"],
  [/^re_/, ["email"], "Resend key"],
  [/^sk-ant-/, ["anthropic"], "Anthropic key"],
  [/^whsec_/, ["stripe_webhook"], "Stripe webhook secret"],
  [/^pk_/, ["stripe_publishable", "fee_stripe_pk"], "Stripe publishable key"],
  [/^(sk|rk)_/, ["stripe", "fee_stripe"], "Stripe secret key"],
];

/** Plain words when a key clearly belongs in another row, else null. */
export function keyMistake(provider: KeyProvider, secret: string): string | null {
  const s = secret.trim();
  const mark = KEY_MARKS.find(([re]) => re.test(s));
  if (!mark || mark[1].includes(provider)) return null;
  return `This looks like the ${mark[2]}. Paste it in that row instead.`;
}

// 5000 fits a Google service account JSON key (the email key, about 2,400 characters).
export const keyPutBody = z.object({ secret: z.string().trim().min(8).max(5000) });

export const keyRow = z.object({ provider: keyProvider, saved: z.literal(true), preview: z.string(), createdAt: z.string() });

/** The Google Maps browser key the maps load with, or null when head office has not saved one. */
export const mapsKeyAnswer = z.object({ key: z.string().nullable() });
export type MapsKeyAnswer = z.infer<typeof mapsKeyAnswer>;
